import { useCallback, useEffect, useRef, useState } from 'react'
import { Box, Button, IconButton, Stack, Tooltip, Typography, alpha, useTheme } from '@mui/material'
import { CallEnd, CloseFullscreen, OpenInFull, Videocam } from '@mui/icons-material'

import { useAppDispatch, showSnackbar } from '@/store'
import { ZOOM_POSTMSG } from './messages'

interface ZoomEncounterCallProps {
  /** Per-appointment room UUID — same value the BE returns on the appointment row. */
  roomId: string
  /** Fired when the call ends (SDK leave or manual). Lets a parent that owns
   *  the mount (e.g. tracks the active room) clear its state so the call can
   *  be reopened. Optional — callers that mount this conditionally on their
   *  own can ignore it. */
  onClose?: () => void
}

const WIDGET_WIDTH = 450
const WIDGET_HEIGHT = 600
const FORCE_CLOSE_TIMEOUT_MS = 1500

export default function ZoomEncounterCall({ roomId, onClose }: ZoomEncounterCallProps) {
  const theme = useTheme()
  const dispatch = useAppDispatch()
  const iframeRef = useRef<HTMLIFrameElement | null>(null)
  const closingRef = useRef(false)
  // Keep onClose in a ref so the message effect can stay mount-only without
  // re-subscribing whenever the parent passes a fresh callback.
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  // fullscreen: covers the page; the call opens and stays here until the user
  // chooses otherwise. minimized: small draggable floating widget bottom-right,
  // entered only via the Minimize button.
  const [videoMode, setVideoMode] = useState<'fullscreen' | 'minimized'>('fullscreen')
  const [callEnded, setCallEnded] = useState(false)
  const [dragPos, setDragPos] = useState<{ top: number; left: number } | null>(null)
  const dragState = useRef<{ offsetX: number; offsetY: number } | null>(null)
  const [isDragging, setIsDragging] = useState(false)

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return
      const { type } = event.data || {}
      if (type && String(type).startsWith('TELEHEALTH_')) {
        console.warn('[telehealth] parent received', type, event.data)
      }
      if (type === ZOOM_POSTMSG.SESSION_CLOSED) {
        setCallEnded(true)
        onCloseRef.current?.()
      } else if (type === ZOOM_POSTMSG.PARTICIPANT_LEFT) {
        // The other party dropped while we're still in the call. Show an
        // app-level toast so it's visible even when the call is minimized.
        dispatch(
          showSnackbar({
            severity: 'info',
            message: event.data?.message || 'The other participant left the call',
          }),
        )
      }
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
    // dispatch is referentially stable (react-redux), so this stays effectively mount-only.
  }, [dispatch])

  // Ask the SDK to leave when this component unmounts (e.g. user navigates
  // away while the call is still active). Iframe is unmounted by React
  // right after, but the postMessage is dispatched synchronously into the
  // still-live child window first.
  useEffect(() => {
    const iframe = iframeRef
    return () => {
      const child = iframe.current?.contentWindow
      if (!child) return
      try {
        child.postMessage({ type: ZOOM_POSTMSG.REQUEST_LEAVE }, window.location.origin)
      } catch {
        // child gone
      }
    }
  }, [])

  useEffect(() => {
    if (videoMode === 'fullscreen') setDragPos(null)
  }, [videoMode])

  const onDragStart = useCallback((event: React.MouseEvent<HTMLElement>) => {
    const widget = (event.currentTarget as HTMLElement).closest('[data-zoom-widget="1"]')
    if (!widget) return
    const rect = widget.getBoundingClientRect()
    dragState.current = {
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
    }
    setDragPos({ top: rect.top, left: rect.left })
    setIsDragging(true)
    event.preventDefault()
  }, [])

  useEffect(() => {
    if (!isDragging) return
    const onMove = (e: MouseEvent) => {
      if (!dragState.current) return
      const nextLeft = e.clientX - dragState.current.offsetX
      const nextTop = e.clientY - dragState.current.offsetY
      const maxLeft = Math.max(0, window.innerWidth - WIDGET_WIDTH)
      const maxTop = Math.max(0, window.innerHeight - WIDGET_HEIGHT)
      setDragPos({
        left: Math.min(Math.max(0, nextLeft), maxLeft),
        top: Math.min(Math.max(0, nextTop), maxTop),
      })
    }
    const onUp = () => {
      dragState.current = null
      setIsDragging(false)
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
    return () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
    }
  }, [isDragging])

  const handleEndCall = useCallback(() => {
    if (closingRef.current) return
    closingRef.current = true
    const child = iframeRef.current?.contentWindow
    if (child) {
      try {
        child.postMessage({ type: ZOOM_POSTMSG.REQUEST_LEAVE }, window.location.origin)
      } catch {
        // child gone
      }
    }
    // Force unmount if the SDK never acks (e.g. iframe still loading).
    // When the SDK does ack, SESSION_CLOSED fires first and onClose runs
    // there; this is only the stalled-iframe fallback.
    setTimeout(() => {
      setCallEnded(true)
      onCloseRef.current?.()
    }, FORCE_CLOSE_TIMEOUT_MS)
  }, [])

  if (callEnded) return null

  return (
    <Box
      data-zoom-widget="1"
      sx={{
        position: 'fixed',
        bgcolor: '#000',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: videoMode === 'minimized' ? 12 : 'none',
        borderRadius: videoMode === 'minimized' ? 1.5 : 0,
        overflow: 'hidden',
        transition: isDragging ? 'none' : 'all 220ms ease',
        ...(videoMode === 'fullscreen'
          ? {
              inset: 0,
              zIndex: (t) => t.zIndex.modal + 2,
            }
          : {
              width: WIDGET_WIDTH,
              height: WIDGET_HEIGHT,
              zIndex: (t) => t.zIndex.modal + 1,
              ...(dragPos ? { top: dragPos.top, left: dragPos.left } : { bottom: 16, right: 16 }),
            }),
      }}
    >
      {videoMode === 'minimized' && (
        <Box
          onMouseDown={onDragStart}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 1.25,
            py: 0.75,
            bgcolor: 'background.paper',
            borderBottom: `1px solid ${theme.palette.divider}`,
            // The header doubles as the drag handle.
            cursor: isDragging ? 'grabbing' : 'grab',
            userSelect: 'none',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Videocam sx={{ fontSize: 16, color: 'primary.main' }} />
            <Typography variant="caption" fontWeight={600}>
              Meeting
            </Typography>
          </Box>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Button
              size="small"
              onClick={() => setVideoMode('fullscreen')}
              onMouseDown={(e) => e.stopPropagation()}
              startIcon={<OpenInFull sx={{ fontSize: 14 }} />}
              sx={{ textTransform: 'none', fontWeight: 600, py: '1px' }}
            >
              Expand
            </Button>
            <Tooltip title="End call">
              <IconButton
                size="small"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={handleEndCall}
                aria-label="End call"
                sx={{
                  color: 'error.main',
                  '&:hover': { bgcolor: (t) => alpha(t.palette.error.main, 0.08) },
                }}
              >
                <CallEnd sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>
      )}

      <Box sx={{ flex: 1, position: 'relative', minHeight: 0 }}>
        <iframe
          ref={iframeRef}
          src={`/telehealth/${encodeURIComponent(roomId)}?embed=1`}
          title="Telehealth call"
          allow="camera; microphone; display-capture; autoplay; clipboard-read; clipboard-write"
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            border: 'none',
            display: 'block',
            // Stops the iframe from swallowing parent mouse events while dragging.
            pointerEvents: isDragging ? 'none' : 'auto',
          }}
        />
        {videoMode === 'fullscreen' && (
          <Stack direction="row" spacing={1} sx={{ position: 'absolute', top: 12, right: 12 }}>
            <Button
              variant="contained"
              size="small"
              onClick={() => setVideoMode('minimized')}
              startIcon={<CloseFullscreen sx={{ fontSize: 14 }} />}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                bgcolor: 'rgba(30,41,59,0.85)',
                '&:hover': { bgcolor: 'rgba(30,41,59,1)' },
              }}
            >
              Minimize
            </Button>

            <Button
              variant="contained"
              size="small"
              color="error"
              onClick={handleEndCall}
              startIcon={<CallEnd sx={{ fontSize: 14 }} />}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              End call
            </Button>
          </Stack>
        )}
      </Box>
    </Box>
  )
}
